import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
  Switch,
  DateField,
} from '@structural-climbing/ui'
import { APP_VERSIONS, compressImage } from '@structural-climbing/ui'
import { PrivacyPolicyLink } from '../features/legal/PrivacyPolicyLink'
import { EmailVerificationCard } from '../features/account/EmailVerificationCard'
import { useProfileCompletion } from '../context/ProfileCompletionContext'
import { normalizePhone } from '@structural-climbing/validation'
import { defaultDateOfBirth } from '../utils/defaultDob'
import { InstallAppButton } from '../features/pwa/InstallAppButton'
import { useMyGyms } from '../features/gym/useMyGyms'
import { memberFullName } from '../features/profile/displayName'
import { ProfileReturnBanner } from '../features/profile/ProfileReturnBanner'
import { fixField, safeReturnTo } from '../features/profile/profileFixLink'
import { PENDING_DOCUMENTS_CHANGED } from '../features/legal/usePendingDocuments'
import { useViewedGym } from '../context/ViewedGymContext'
import { Page } from '../design/Page'
import { SurfaceCard } from '../design/SurfaceCard'
import { SkeletonCard } from '../design/Skeleton'
import { Sheet } from '../design/Sheet'
import { SettingsRow } from '../features/profile/SettingsRow'
import { useMyProfile } from '../features/profile/useMyProfile'
import { useMySubscription } from '../features/membership/useMySubscription'
import type { SubscriptionStatus } from '@structural-climbing/ui'

interface Profile {
  firstName: string | null
  lastName: string | null
  nickname: string | null
  avatarUrl: string | null
  isPublic: boolean
  language: string
  preferredGymId: string | null
  phone: string | null
  dateOfBirth: string | null
  gender: string | null
  emailReminders: boolean
  /** Not ended yet. Their standings are public, so the profile stays public. */
  activeCompetitions?: { name: string; slug: string }[]
}

const DETAIL_FIELDS = [
  'firstName',
  'lastName',
  'nickname',
  'phone',
  'dateOfBirth',
  'gender',
] as const
type DetailField = (typeof DETAIL_FIELDS)[number]

/** Each language in its own words, the way a language picker always shows them. */
const LANGUAGES = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
] as const

const STATUS_KEY: Record<SubscriptionStatus, string> = {
  active: 'membership.statusActive',
  scheduled: 'membership.statusScheduled',
  frozen: 'membership.statusFrozen',
  expired: 'membership.statusExpired',
  depleted: 'membership.statusDepleted',
  cancelled: 'membership.statusCancelled',
}

interface Props {
  /**
   * `details` is the personal-details form on its own page
   * (`/profile/details`), where the competition and gym links send a member
   * to fill in one field. Without it, the settings list.
   */
  section?: 'details'
}

export function ProfilePage({ section }: Props = {}) {
  const { isAuthenticated, user, logout, getAccessTokenSilently } = useAuth()

  // Profile carries no gym in its URL, so the gym being shown is whichever one
  // the member last opened, falling back to their first.
  const { gyms: myGyms } = useMyGyms()
  const { gym: viewedGym } = useViewedGym()
  const activeGym = myGyms.find((g) => g.slug === viewedGym?.slug) ?? myGyms[0] ?? null
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { refresh: refreshCompletion } = useProfileCompletion()
  // Same cached reads the dashboard makes, so coming from Home costs nothing.
  const { profile: myProfile } = useMyProfile()
  const { subscription } = useMySubscription(section ? null : activeGym?.id)

  const [profile, setProfile] = useState<Profile | undefined>(undefined)
  const [sheet, setSheet] = useState<'language' | 'notifications' | 'visibility' | null>(null)
  const [firstName, setFirstName] = useState('')
  const [savedFirstName, setSavedFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [savedLastName, setSavedLastName] = useState('')
  const [nickname, setNickname] = useState('')
  const [savedNickname, setSavedNickname] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [savedAvatarUrl, setSavedAvatarUrl] = useState<string | null>(null)
  const [isPublic, setIsPublic] = useState(true)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [visibilityError, setVisibilityError] = useState<string | null>(null)
  const [competing, setCompeting] = useState<{ name: string; slug: string }[]>([])
  const [languageError, setLanguageError] = useState<string | null>(null)
  const [phone, setPhone] = useState('')
  const [savedPhone, setSavedPhone] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [savedDateOfBirth, setSavedDateOfBirth] = useState('')
  // A native date input with no value opens on the current month, leaving a new
  // member to scroll back decades. Seed the same starting point the join form
  // uses, but remember it is only a suggestion so blurring an untouched field
  // never saves a birthday nobody chose.
  const [dateOfBirthSuggested, setDateOfBirthSuggested] = useState(false)
  const [gender, setGender] = useState<string | null>(null)
  const [savedGender, setSavedGender] = useState<string | null>(null)
  const [emailReminders, setEmailReminders] = useState(true)
  const [emailRemindersError, setEmailRemindersError] = useState<string | null>(null)
  // The details are one form with one Save. Each field used to save itself on
  // blur, which gave no sign that anything had happened and no way to back out
  // of a half-typed change.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<DetailField, string>>>({})
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Sent here to fill in one field, e.g. from a competition that needs a
  // gender: go straight to it, light it up, and offer the way back.
  const [searchParams] = useSearchParams()
  const focusField = fixField(searchParams.get('field'))
  const returnTo = safeReturnTo(searchParams.get('returnTo'))
  const genderRef = useRef<HTMLDivElement>(null)
  const dobRef = useRef<HTMLDivElement>(null)
  const phoneRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isAuthenticated) return
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const profileRes = await fetch(`${apiUrl}/my-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (profileRes.ok) {
          const data = (await profileRes.json()) as Profile
          setProfile(data)
          setFirstName(data.firstName ?? '')
          setSavedFirstName(data.firstName ?? '')
          setLastName(data.lastName ?? '')
          setSavedLastName(data.lastName ?? '')
          setNickname(data.nickname ?? '')
          setSavedNickname(data.nickname ?? '')
          setAvatarUrl(data.avatarUrl)
          setSavedAvatarUrl(data.avatarUrl)
          setIsPublic(data.isPublic)
          setCompeting(data.activeCompetitions ?? [])
          setPhone(data.phone ?? '')
          setSavedPhone(data.phone ?? '')
          setDateOfBirth(data.dateOfBirth ?? defaultDateOfBirth())
          setDateOfBirthSuggested(!data.dateOfBirth)
          setSavedDateOfBirth(data.dateOfBirth ?? '')
          setGender(data.gender)
          setSavedGender(data.gender)
          setEmailReminders(data.emailReminders ?? true)
          void i18n.changeLanguage(data.language ?? 'en')
        } else {
          setProfile({
            firstName: null,
            lastName: null,
            nickname: null,
            avatarUrl: null,
            isPublic: true,
            language: 'en',
            preferredGymId: null,
            phone: null,
            dateOfBirth: null,
            gender: null,
            emailReminders: true,
          })
        }
      } catch {
        setProfile({
          firstName: null,
          lastName: null,
          nickname: null,
          avatarUrl: null,
          isPublic: true,
          language: 'en',
          preferredGymId: null,
          phone: null,
          dateOfBirth: null,
          gender: null,
          emailReminders: true,
        })
      }
    }
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  function handleAvatarClick() {
    fileInputRef.current?.click()
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadError(null)
    setIsUploading(true)
    try {
      const compressed = await compressImage(file, 2 * 1024 * 1024)
      const token = await getAccessTokenSilently()
      const formData = new FormData()
      formData.append('file', compressed)
      const res = await fetch(`${apiUrl}/my-avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      const data = (await res.json()) as { url?: string; error?: string }
      if (!res.ok) {
        setUploadError(data.error ?? t('common.errorDefault'))
        return
      }
      setAvatarUrl(data.url ?? null)
      setSavedAvatarUrl(data.url ?? null)
      refreshCompletion()
    } catch {
      setUploadError(t('common.networkError'))
    } finally {
      setIsUploading(false)
    }
  }

  // Typing in a field takes back its own error and the "saved" note: neither
  // is true of what is on screen any more.
  function touched(field: DetailField) {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
    setFormError(null)
    setJustSaved(false)
  }

  const typedFirstName = firstName.trim()
  const typedLastName = lastName.trim()
  const typedNickname = nickname.trim()
  const typedPhone = phone.trim()
  // The suggested starting date is not a choice, so it is not a change either.
  const dateOfBirthChanged = !dateOfBirthSuggested && dateOfBirth !== savedDateOfBirth
  const dirty =
    typedFirstName !== savedFirstName ||
    typedLastName !== savedLastName ||
    typedNickname !== savedNickname ||
    typedPhone !== savedPhone ||
    dateOfBirthChanged ||
    gender !== savedGender

  // Leaving the tab with a change still unsaved gets the browser's own warning.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function handleDiscard() {
    setFirstName(savedFirstName)
    setLastName(savedLastName)
    setNickname(savedNickname)
    setPhone(savedPhone)
    setDateOfBirth(savedDateOfBirth || defaultDateOfBirth())
    setDateOfBirthSuggested(!savedDateOfBirth)
    setGender(savedGender)
    setFieldErrors({})
    setFormError(null)
  }

  async function handleSave() {
    const errors: Partial<Record<DetailField, string>> = {}
    // Only what changed is sent, so a field the member never touched cannot be
    // refused on their behalf.
    const body: Record<string, unknown> = {}

    // A name or nickname that was saved can be changed, not blanked.
    if (typedFirstName !== savedFirstName) {
      if (typedFirstName) body.firstName = typedFirstName
      else errors.firstName = t('profile.requiredError')
    }
    if (typedLastName !== savedLastName) {
      if (typedLastName) body.lastName = typedLastName
      else errors.lastName = t('profile.requiredError')
    }
    if (typedNickname !== savedNickname) {
      if (typedNickname) body.nickname = typedNickname
      else errors.nickname = t('profile.requiredError')
    }

    // Every member has a phone number, so an empty one stops the save even if
    // it was never filled in. A saved number left alone is not re-judged.
    let nextPhone = savedPhone
    if (!typedPhone) {
      errors.phone = t('profile.phoneRequiredError')
    } else if (typedPhone !== savedPhone) {
      const normalized = normalizePhone(typedPhone)
      if (!normalized) errors.phone = t('join.phoneInvalidError')
      else {
        nextPhone = normalized
        if (normalized !== savedPhone) body.phone = normalized
      }
    }

    if (dateOfBirthChanged) body.dateOfBirth = dateOfBirth || null
    if (gender !== savedGender) body.gender = gender

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setFormError(t('profile.fixFieldsError'))
      return
    }

    setSaving(true)
    setFieldErrors({})
    setFormError(null)
    try {
      if (Object.keys(body).length > 0) {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/my-profile`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const data = (await res.json()) as { error?: string; resignRequired?: boolean }
        if (!res.ok) {
          // The server names the field it refused at the start of its message.
          const field = DETAIL_FIELDS.find((f) => data.error?.startsWith(f))
          if (field && data.error) setFieldErrors({ [field]: data.error })
          else setFormError(data.error ?? t('common.errorDefault'))
          return
        }
        // A new name retires the documents signed under the old one. The gate
        // is far from here, in the root layout, so it is told to look again.
        if (data.resignRequired) window.dispatchEvent(new Event(PENDING_DOCUMENTS_CHANGED))
      }
      // Show what was stored: trimmed, and the phone in its one format.
      setFirstName(typedFirstName)
      setSavedFirstName(typedFirstName)
      setLastName(typedLastName)
      setSavedLastName(typedLastName)
      setNickname(typedNickname)
      setSavedNickname(typedNickname)
      setPhone(nextPhone)
      setSavedPhone(nextPhone)
      if (dateOfBirthChanged) setSavedDateOfBirth(dateOfBirth)
      setSavedGender(gender)
      setJustSaved(true)
      refreshCompletion()
    } catch {
      setFormError(t('common.networkError'))
    } finally {
      setSaving(false)
    }
  }

  async function handleVisibilityChange(value: boolean) {
    setIsPublic(value)
    setVisibilityError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: value }),
      })
      const data = (await res.json()) as {
        error?: string
        code?: string
        competitions?: { name: string; slug: string }[]
      }
      if (!res.ok) {
        setIsPublic(!value)
        if (data.code === 'IN_COMPETITION') setCompeting(data.competitions ?? [])
        else setVisibilityError(data.error ?? t('common.errorDefault'))
      }
    } catch {
      setIsPublic(!value)
      setVisibilityError(t('common.networkError'))
    }
  }

  async function handleEmailRemindersChange(value: boolean) {
    setEmailReminders(value)
    setEmailRemindersError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailReminders: value }),
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setEmailReminders(!value)
        setEmailRemindersError(data.error ?? t('common.errorDefault'))
      }
    } catch {
      setEmailReminders(!value)
      setEmailRemindersError(t('common.networkError'))
    }
  }

  async function handleLanguageChange(lang: string) {
    setLanguageError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: lang }),
      })
      const data = (await res.json()) as { error?: string }
      if (!res.ok) {
        setLanguageError(data.error ?? t('common.errorDefault'))
      }
    } catch {
      setLanguageError(t('common.networkError'))
    }
  }

  const loaded = profile !== undefined
  useEffect(() => {
    if (!loaded || !focusField) return
    const target =
      focusField === 'gender'
        ? genderRef.current
        : focusField === 'phone'
          ? phoneRef.current
          : dobRef.current
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  }, [loaded, focusField])

  // Only the categories' own values count: "prefer not to say" is a saved
  // answer, but it still cannot place anyone in a men's or women's category.
  const fieldDone =
    focusField === 'gender'
      ? savedGender === 'male' || savedGender === 'female'
      : focusField === 'dob'
        ? !!savedDateOfBirth
        : focusField === 'phone'
          ? !!savedPhone
          : false
  // Lit until it is filled, so the member never loses which field it was.
  // Drawn on the field itself, so it follows the field's own corners.
  const highlightFor = (field: 'gender' | 'dob' | 'phone') =>
    focusField === field && !fieldDone
      ? 'ring-2 ring-primary ring-offset-2 ring-offset-content1'
      : ''
  const hintFor = (field: 'gender' | 'dob') =>
    focusField === field && !fieldDone ? t('profile.neededForCompetition') : undefined

  if (!section && searchParams.get('field')) {
    // Older links asked for a field on the profile itself. The fields live on
    // their own page now, so pass the request on, returnTo and all.
    return <Navigate to={`/profile/details?${searchParams.toString()}`} replace />
  }

  if (profile === undefined) {
    return (
      <Page>
        <SkeletonCard lines={3} />
        <SkeletonCard lines={4} />
      </Page>
    )
  }

  // The account's own name, not the Auth0 one: a saved change has to show here
  // immediately.
  const accountName = memberFullName(
    { firstName: savedFirstName, lastName: savedLastName },
    user,
    '',
  )
  const initial = (accountName || 'M')[0].toUpperCase()

  // What is still empty, by name, so "86%" never needs explaining.
  const missing = [
    !savedAvatarUrl && t('profile.photoLabel'),
    !savedFirstName && t('profile.firstNameLabel'),
    !savedLastName && t('profile.lastNameLabel'),
    !savedNickname && t('profile.nicknameLabel'),
    !savedPhone && t('profile.phoneLabel'),
    !savedDateOfBirth && t('profile.dateOfBirthLabel'),
    !savedGender && t('profile.genderLabel'),
  ].filter((x): x is string => Boolean(x))
  const detailsMissing = missing.length - (savedAvatarUrl ? 0 : 1) > 0
  const emailUnverified = myProfile?.emailVerified === false

  const genderOptions = [
    { key: 'male', label: t('profile.genderMale') },
    { key: 'female', label: t('profile.genderFemale') },
    { key: 'prefer_not_to_say', label: t('profile.genderPreferNotToSay') },
  ]

  if (section === 'details') {
    return (
      <Page>
        <Link
          to="/profile"
          className="-mb-2 self-start text-sm font-medium text-foreground/55 hover:text-foreground"
        >
          ‹ {t('profile.title')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('profile.detailsTitle')}</h1>

        {focusField && returnTo && (
          <ProfileReturnBanner done={fieldDone} returnTo={returnTo} field={focusField} />
        )}

        <EmailVerificationCard />

        <SurfaceCard className="p-5">
          <form
            noValidate
            className="flex flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSave()
            }}
          >
            {/* First name */}
            <div className="flex flex-col gap-1">
              <Input
                label={t('profile.firstNameLabel')}
                placeholder={t('profile.namePlaceholder')}
                value={firstName}
                onValueChange={(v) => {
                  setFirstName(v)
                  touched('firstName')
                }}
                maxLength={100}
                isInvalid={!!fieldErrors.firstName}
              />
              {fieldErrors.firstName && (
                <p className="text-sm text-danger">{fieldErrors.firstName}</p>
              )}
            </div>

            {/* Last name */}
            <div className="flex flex-col gap-1">
              <Input
                label={t('profile.lastNameLabel')}
                placeholder={t('profile.lastNamePlaceholder')}
                value={lastName}
                onValueChange={(v) => {
                  setLastName(v)
                  touched('lastName')
                }}
                maxLength={100}
                isInvalid={!!fieldErrors.lastName}
              />
              {fieldErrors.lastName && (
                <p className="text-sm text-danger">{fieldErrors.lastName}</p>
              )}
            </div>

            {/* Nickname */}
            <div className="flex flex-col gap-1">
              <Input
                label={t('profile.nicknameLabel')}
                placeholder={t('profile.nicknamePlaceholder')}
                value={nickname}
                onValueChange={(v) => {
                  setNickname(v)
                  touched('nickname')
                }}
                maxLength={30}
                isInvalid={!!fieldErrors.nickname}
              />
              {fieldErrors.nickname && (
                <p className="text-sm text-danger">{fieldErrors.nickname}</p>
              )}
            </div>

            {/* Phone */}
            <div ref={phoneRef} className="flex flex-col gap-1">
              <Input
                label={t('profile.phoneLabel')}
                placeholder={t('profile.phonePlaceholder')}
                type="tel"
                isRequired
                className={highlightFor('phone')}
                description={
                  focusField === 'phone' && !fieldDone ? t('profile.neededByGym') : undefined
                }
                value={phone}
                onValueChange={(v) => {
                  setPhone(v)
                  touched('phone')
                }}
                maxLength={20}
                isInvalid={!!fieldErrors.phone}
              />
              {fieldErrors.phone && <p className="text-sm text-danger">{fieldErrors.phone}</p>}
            </div>

            {/* Date of birth */}
            <div ref={dobRef} className="flex flex-col gap-1">
              <DateField
                label={t('profile.dateOfBirthLabel')}
                classNames={{ inputWrapper: highlightFor('dob') }}
                description={hintFor('dob')}
                value={dateOfBirth}
                onValueChange={(v) => {
                  setDateOfBirth(v)
                  setDateOfBirthSuggested(false)
                  touched('dateOfBirth')
                }}
              />
              {fieldErrors.dateOfBirth && (
                <p className="text-sm text-danger">{fieldErrors.dateOfBirth}</p>
              )}
            </div>

            {/* Gender */}
            <div ref={genderRef} className="flex flex-col gap-1">
              <Select
                label={t('profile.genderLabel')}
                classNames={{ trigger: highlightFor('gender') }}
                description={hintFor('gender')}
                selectedKeys={gender ? new Set([gender]) : new Set<string>()}
                onSelectionChange={(keys) => {
                  const key = [...keys][0] as string | undefined
                  setGender(key ?? null)
                  touched('gender')
                }}
              >
                {genderOptions.map((opt) => (
                  <SelectItem key={opt.key}>{opt.label}</SelectItem>
                ))}
              </Select>
              {fieldErrors.gender && <p className="text-sm text-danger">{fieldErrors.gender}</p>}
            </div>

            {/* One line that always says where things stand, then the buttons. */}
            <div className="flex flex-col gap-3 border-t border-[var(--gym-line)] pt-5">
              {formError && (
                <p role="alert" className="text-sm text-danger">
                  {formError}
                </p>
              )}
              {!formError && (dirty || justSaved) && (
                <p
                  role="status"
                  className={`text-sm ${dirty ? 'text-foreground/60' : 'font-medium text-success'}`}
                >
                  {dirty ? t('profile.unsavedChanges') : t('profile.changesSaved')}
                </p>
              )}
              <div className="flex gap-2">
                {dirty && (
                  <Button variant="flat" isDisabled={saving} onPress={handleDiscard}>
                    {t('profile.discardChanges')}
                  </Button>
                )}
                <Button
                  type="submit"
                  color="primary"
                  className="flex-1 font-semibold"
                  isLoading={saving}
                  isDisabled={!dirty}
                >
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </form>
        </SurfaceCard>
      </Page>
    )
  }

  const languageName = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage)?.label

  return (
    <Page>
      <h1 className="sr-only">{t('profile.title')}</h1>

      {/* Who this account is, before any of its settings. */}
      <div className="flex flex-col items-center gap-1.5 pt-2 text-center">
        <div className="relative">
          <Button
            isIconOnly
            variant="flat"
            aria-label={t('profile.changeAvatar')}
            onPress={handleAvatarClick}
            isDisabled={isUploading}
            className="h-24 w-24 overflow-hidden rounded-full bg-[var(--gym-primary)] text-3xl font-bold text-[var(--gym-on-primary)]"
          >
            {isUploading ? (
              <Spinner size="sm" color="white" />
            ) : avatarUrl ? (
              <img
                src={avatarUrl}
                alt={t('profile.avatarAlt')}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>{initial}</span>
            )}
          </Button>
          {/* No photo yet: a camera on the circle says it can take one. */}
          {!avatarUrl && !isUploading && (
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--gym-page)] bg-content1 text-foreground/70 shadow-e1"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
            </span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => void handleFileChange(e)}
        />
        {uploadError && <p className="text-sm text-danger">{uploadError}</p>}
        <p className="mt-1.5 text-xl font-bold">{accountName}</p>
        {user?.email && <p className="text-sm text-foreground/50">{user.email}</p>}
        {missing.length > 0 && (
          <p className="text-sm font-medium text-danger">
            {t('profile.missingFields', {
              fields: missing.map((m) => m.toLocaleLowerCase(i18n.language)).join(', '),
            })}
          </p>
        )}
      </div>

      <SurfaceCard className="flex flex-col divide-y divide-[var(--gym-line)] overflow-hidden p-0">
        <SettingsRow
          label={t('profile.detailsTitle')}
          to="/profile/details"
          dot={(detailsMissing || emailUnverified) && 'attention'}
        />
        {activeGym && (
          <SettingsRow
            label={t('profile.myPlan')}
            // Home, with the code sheet open: the plan's name, validity and
            // status sit beside the QR there, which is what this row is for.
            to={`/${activeGym.slug}/dashboard?code=me`}
            value={subscription ? t(STATUS_KEY[subscription.effective_status]) : undefined}
          />
        )}
        {activeGym && <SettingsRow label={t('family.menuLink')} to={`/${activeGym.slug}/family`} />}
        <SettingsRow
          label={t('profile.languageLabel')}
          value={languageName}
          onPress={() => setSheet('language')}
        />
        <SettingsRow
          label={t('profile.notificationsLabel')}
          value={emailReminders ? t('profile.notificationsOn') : t('profile.notificationsOff')}
          onPress={() => setSheet('notifications')}
        />
        <SettingsRow
          label={t('profile.visibilityLabel')}
          value={isPublic ? t('profile.visibilityPublic') : t('profile.visibilityPrivate')}
          onPress={() => setSheet('visibility')}
        />
      </SurfaceCard>

      <InstallAppButton fullWidth />

      <SurfaceCard className="overflow-hidden p-0">
        <SettingsRow
          label={t('common.signOut')}
          danger
          onPress={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </SurfaceCard>
      <p className="text-center text-tiny text-foreground/45">v{APP_VERSIONS.member}</p>
      <PrivacyPolicyLink className="self-center" />

      <Sheet
        isOpen={sheet === 'language'}
        onClose={() => setSheet(null)}
        aria-label={t('profile.languageLabel')}
      >
        <div className="flex flex-col gap-3 px-4 pb-6 pt-1">
          <h2 className="text-xl font-bold tracking-tight">{t('profile.languageLabel')}</h2>
          <div className="flex flex-col divide-y divide-[var(--gym-line)] overflow-hidden rounded-2xl border border-[var(--gym-line)]">
            {LANGUAGES.map((lang) => {
              const on = lang.code === i18n.resolvedLanguage
              return (
                <Button
                  key={lang.code}
                  variant="light"
                  disableRipple
                  aria-pressed={on}
                  className="h-14 w-full justify-between rounded-none px-5 text-[15px] font-normal"
                  onPress={() => {
                    void i18n.changeLanguage(lang.code)
                    void handleLanguageChange(lang.code)
                    setSheet(null)
                  }}
                >
                  {lang.label}
                  {on && (
                    <svg
                      aria-hidden
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-5 w-5 text-[var(--gym-primary)]"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </Button>
              )
            })}
          </div>
          {languageError && <p className="text-sm text-danger">{languageError}</p>}
        </div>
      </Sheet>

      <Sheet
        isOpen={sheet === 'notifications'}
        onClose={() => setSheet(null)}
        aria-label={t('profile.notificationsLabel')}
      >
        <div className="flex flex-col gap-3 px-4 pb-6 pt-1">
          <h2 className="text-xl font-bold tracking-tight">{t('profile.notificationsLabel')}</h2>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-[var(--gym-line)] px-5 py-4">
            <div className="min-w-0">
              <p className="text-[15px]">{t('profile.emailRemindersLabel')}</p>
              <p className="mt-0.5 text-xs text-foreground/50">{t('profile.emailRemindersDesc')}</p>
            </div>
            <Switch
              isSelected={emailReminders}
              onValueChange={(v) => void handleEmailRemindersChange(v)}
              aria-label={t('profile.emailRemindersLabel')}
            />
          </div>
          {emailRemindersError && <p className="text-sm text-danger">{emailRemindersError}</p>}
        </div>
      </Sheet>

      <Sheet
        isOpen={sheet === 'visibility'}
        onClose={() => setSheet(null)}
        aria-label={t('profile.visibilityLabel')}
      >
        <div className="flex flex-col gap-3 px-4 pb-6 pt-1">
          <h2 className="text-xl font-bold tracking-tight">{t('profile.visibilityLabel')}</h2>
          <div className="flex gap-2">
            <Button
              variant={!isPublic ? 'solid' : 'flat'}
              color={!isPublic ? 'primary' : 'default'}
              isDisabled={isPublic && competing.length > 0}
              onPress={() => void handleVisibilityChange(false)}
              className="flex-1"
            >
              {t('profile.visibilityPrivate')}
            </Button>
            <Button
              variant={isPublic ? 'solid' : 'flat'}
              color={isPublic ? 'primary' : 'default'}
              onPress={() => void handleVisibilityChange(true)}
              className="flex-1"
            >
              {t('profile.visibilityPublic')}
            </Button>
          </div>
          <p className="text-xs text-foreground/50">
            {isPublic ? t('profile.visibilityPublicDesc') : t('profile.visibilityPrivateDesc')}
          </p>
          {isPublic && competing.length > 0 && (
            <div className="flex flex-col gap-1 rounded-xl bg-primary/5 p-3">
              <p className="text-sm font-medium">
                {t('profile.visibilityCompeting', {
                  name: competing.map((c) => c.name).join(', '),
                })}
              </p>
              <p className="text-xs text-foreground/60">{t('profile.visibilityCompetingHint')}</p>
              <Link
                to={`/competitions/${competing[0].slug}`}
                className="text-sm font-medium text-primary"
              >
                {t('profile.visibilityCompetingLink')} →
              </Link>
            </div>
          )}
          {visibilityError && <p className="text-sm text-danger">{visibilityError}</p>}
        </div>
      </Sheet>
    </Page>
  )
}
